import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaFileAlt, FaArrowLeft } from 'react-icons/fa';
import Swal from 'sweetalert2';
import * as previredService from '../../services/previredService';
import TrabajadorPrevired from '../../components/RRHH/TrabajadorPrevired';
import '../../styles/pages/RRHH/PreviredDetallePage.css';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatCLP = (val) => `$${Math.round(Number(val || 0)).toLocaleString('es-CL')}`;
const formatFecha = (val) => (val ? new Date(val).toLocaleDateString('es-CL') : '—');

/**
 * Agrupa las líneas de asiento en bloques consecutivos: un devengado por trabajador,
 * el pago del líquido y el pago Previred. Llegan en el orden en que se crearon.
 */
function agruparAsientos(asientos, colaboradores) {
    const nombrePorLiquidacion = Object.fromEntries(colaboradores.map(c => [c.id_liquidacion, c.nombre]));
    const bloques = [];

    for (const linea of asientos) {
        const clave = `${linea.grupo}-${linea.grupo === 'Devengado' ? linea.id_registro : ''}`;
        let bloque = bloques[bloques.length - 1];
        if (!bloque || bloque.clave !== clave) {
            const titulo = linea.grupo === 'Devengado'
                ? `Devengado · ${nombrePorLiquidacion[linea.id_registro] || 'liquidación ' + linea.id_registro}`
                : linea.grupo;
            bloque = { clave, titulo, lineas: [] };
            bloques.push(bloque);
        }
        bloque.lineas.push(linea);
    }
    return bloques;
}

/**
 * Ver detalle de la carga de Previred de un período: lo que se contabilizó.
 *
 * Una carga guardada siempre cuadró, así que la pantalla no busca diferencias: muestra
 * la carga, sus asientos y cada trabajador contra su liquidación. Es de solo lectura
 * y llega con el período de la página Previred.
 */
const PreviredDetallePage = ({ id_company }) => {
    const { anio, mes } = useParams();
    const navigate = useNavigate();
    const [datos, setDatos] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelado = false;
        (async () => {
            try {
                setLoading(true);
                const data = await previredService.getDetalle(id_company, mes, anio);
                if (!cancelado) setDatos(data);
            } catch (error) {
                console.error('Error al cargar el detalle:', error);
                Swal.fire('Error', error.response?.data?.message || 'No se pudo cargar el detalle', 'error');
            } finally {
                if (!cancelado) setLoading(false);
            }
        })();
        return () => { cancelado = true; };
    }, [id_company, mes, anio]);

    const volver = () => navigate('/rrhh/previred', {
        state: { periodo: { mes: Number(mes), anio: Number(anio) } }
    });

    const periodo = `${MESES[Number(mes) - 1]} ${anio}`;

    const renderContenido = () => {
        if (loading) return <div className="detalle-vacio">Cargando detalle...</div>;
        if (!datos?.hay_carga) {
            return <div className="detalle-vacio">No hay un certificado de Previred cargado para {periodo}.</div>;
        }

        const { cabecera, asientos, colaboradores } = datos;
        const totalDebe = asientos.reduce((s, a) => s + Number(a.debe || 0), 0);
        const totalHaber = asientos.reduce((s, a) => s + Number(a.haber || 0), 0);
        const pagoPrevired = asientos.find(a => a.grupo === 'Pago Previred' && Number(a.haber) > 0);
        // Qué se le pagó a cada acreedor: las líneas al debe del pago Previred.
        const desglosePrevired = asientos
            .filter(a => a.grupo === 'Pago Previred' && Number(a.debe) > 0)
            .map(a => `${a.cuenta.replace(' POR PAGAR', '')} ${formatCLP(a.debe)}`)
            .join(' · ');

        return (
            <>
                <section className="detalle-bloque">
                    <h2>La carga</h2>
                    <dl className="detalle-resumen">
                        <div>
                            <dt>Archivo</dt>
                            <dd>
                                {cabecera.nombre_archivo}
                                {cabecera.pdf_url && (
                                    <> · <a href={cabecera.pdf_url} target="_blank" rel="noopener noreferrer">ver PDF</a></>
                                )}
                            </dd>
                        </div>
                        <div><dt>Empleador</dt><dd>{cabecera.empleador_razon_social || '—'}</dd></div>
                        <div><dt>Fecha de pago</dt><dd>{formatFecha(cabecera.fecha_pago)}</dd></div>
                        <div><dt>Trabajadores</dt><dd>{colaboradores.length}</dd></div>
                        <div><dt>Total pagado a Previred</dt><dd>{pagoPrevired ? formatCLP(pagoPrevired.haber) : '—'}</dd></div>
                        <div><dt>Pagado por institución</dt><dd>{desglosePrevired || '—'}</dd></div>
                    </dl>
                </section>

                <section className="detalle-bloque">
                    <h2>Asientos generados · {formatFecha(cabecera.fecha_pago)}</h2>
                    <div className="detalle-tabla">
                        <table>
                            <thead>
                                <tr><th>Cuenta</th><th>Debe</th><th>Haber</th></tr>
                            </thead>
                            <tbody>
                                {agruparAsientos(asientos, colaboradores).map(bloque => (
                                    <React.Fragment key={bloque.clave}>
                                        <tr className="fila-grupo"><td colSpan="3">{bloque.titulo}</td></tr>
                                        {bloque.lineas.map(linea => (
                                            <tr key={linea.id}>
                                                <td>{linea.cuenta}</td>
                                                <td className="num">{linea.debe ? formatCLP(linea.debe) : ''}</td>
                                                <td className="num">{linea.haber ? formatCLP(linea.haber) : ''}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                                <tr className="fila-total">
                                    <td>Total</td>
                                    <td className="num">{formatCLP(totalDebe)}</td>
                                    <td className="num">{formatCLP(totalHaber)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="detalle-bloque">
                    <h2>Trabajadores</h2>
                    <div className="detalle-trabajadores">
                        {colaboradores.map(trabajador => (
                            <TrabajadorPrevired key={trabajador.rut} trabajador={trabajador} />
                        ))}
                    </div>
                </section>
            </>
        );
    };

    return (
        <div className="detalle-container">
            <div className="detalle-header">
                <h1><FaFileAlt /> Detalle Previred · {periodo}</h1>
                <button className="btn-volver" onClick={volver}>
                    <FaArrowLeft /> Volver a Previred
                </button>
            </div>
            {renderContenido()}
        </div>
    );
};

export default PreviredDetallePage;
